import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Logo from '../components/Logo';
import { Mail, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VerifyEmail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { checkAuth } = useAuth();
  const initialEmail = location.state?.email || '';
  const previewCode = location.state?.previewCode || null;
  const deliveryMode = location.state?.deliveryMode || 'email';
  const initialRole = location.state?.role || '';
  const initialPendingApproval = !!location.state?.pendingApproval;

  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState(previewCode || '');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [previewBanner, setPreviewBanner] = useState(deliveryMode === 'preview' && !!previewCode);
  const [pendingApprovalDone, setPendingApprovalDone] = useState(false);
  const [approvalRole, setApprovalRole] = useState(initialRole);

  useEffect(() => {
    if (!initialEmail) {
      // If they hit /verify directly, allow them to type the email manually.
    }
  }, [initialEmail]);

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    if (!email) { setError('Email is required'); return; }
    if (!code || code.length < 6) { setError('Enter the 6-digit code from your email'); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/verify-email`, { email, code }, { withCredentials: true });
      if (data.pending_approval) {
        setPendingApprovalDone(true);
        setApprovalRole(data.role || initialRole);
        toast.success('Email verified — awaiting admin approval.');
        setLoading(false);
        return;
      }
      toast.success('Email verified — welcome!');
      // /auth/verify-email also sets cookies, so we can refresh user
      await checkAuth();
      // Route by role
      const role = data.role;
      const adminRoles = ['admin', 'super_admin', 'manager', 'service_manager', 'construction_manager', 'office_staff'];
      if (adminRoles.includes(role)) navigate('/admin/dashboard');
      else if (role === 'employee' || role === 'technician') navigate('/employee/timeclock');
      else if (role === 'vip') navigate('/vip/dashboard');
      else navigate('/customer/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Verification failed');
    }
    setLoading(false);
  };

  const handleResend = async () => {
    if (!email) { setError('Enter your email first'); return; }
    setResending(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/auth/resend-verification`, { email });
      if (data.verification_code) {
        setCode(data.verification_code);
        setPreviewBanner(true);
        toast.success('New code generated (preview mode — auto-filled).');
      } else if (data.verified) {
        toast.success('Already verified — please sign in.');
        navigate('/login');
      } else {
        toast.success('A new code is on its way.');
      }
    } catch {
      toast.error('Could not resend code');
    }
    setResending(false);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8 flex flex-col items-center">
          <Logo size="lg" />
          <p className="text-[#D4AF37] text-xs font-bold tracking-[0.3em] mt-4 mb-2">VERIFY YOUR EMAIL</p>
        </div>

        <div className="bg-[#141414] border border-[#262626] rounded-md p-8">
          {pendingApprovalDone ? (
            <div data-testid="pending-approval-card">
              <div className="flex justify-center mb-4">
                <div className="w-14 h-14 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center">
                  <ShieldCheck className="w-6 h-6 text-[#D4AF37]" />
                </div>
              </div>
              <p className="text-white text-center font-medium mb-1">Email verified.</p>
              <p className="text-[#A3A3A3] text-sm text-center mb-6">
                Thanks {email ? <span className="text-white">{email}</span> : null}. Your {approvalRole === 'vip' ? 'VIP' : 'Employee'} account is now awaiting <span className="text-[#D4AF37]">admin approval</span>. You'll get an email and be able to sign in as soon as you're approved.
              </p>
              <Link to="/login">
                <Button data-testid="pending-back-to-login" className="w-full bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">
                  Back to Sign In
                </Button>
              </Link>
            </div>
          ) : (<>
          <div className="flex justify-center mb-4">
            <div className="w-14 h-14 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center">
              <Mail className="w-6 h-6 text-[#D4AF37]" />
            </div>
          </div>
          <p className="text-white text-center font-medium mb-1">Check your inbox</p>
          <p className="text-[#A3A3A3] text-sm text-center mb-6">We sent a 6-digit verification code to your email. Enter it below to activate your account.</p>

          {previewBanner && (
            <div data-testid="preview-code-banner" className="bg-[#D4AF37]/10 border border-[#D4AF37]/40 rounded-md p-3 mb-4">
              <p className="text-[#D4AF37] text-xs font-bold uppercase tracking-wider">Preview Mode</p>
              <p className="text-white text-sm">Email delivery is disabled in this environment. Your verification code is shown above for testing only.</p>
            </div>
          )}

          <form onSubmit={handleVerify} className="space-y-5">
            {error && (
              <div data-testid="verify-error" className="bg-[#7F1D1D] text-white p-3 rounded-md text-sm">{error}</div>
            )}
            <div className="space-y-2">
              <Label htmlFor="vemail" className="text-white">Email</Label>
              <Input
                data-testid="verify-email-input"
                id="vemail"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-[#0A0A0A] border-[#262626] text-white"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="vcode" className="text-white">Verification Code</Label>
              <Input
                data-testid="verify-code-input"
                id="vcode"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                required
                className="bg-[#0A0A0A] border-[#262626] text-white text-2xl tracking-[0.5em] text-center font-mono"
              />
            </div>
            <Button
              data-testid="verify-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-medium h-11"
            >
              <ShieldCheck className="w-4 h-4 mr-2" />
              {loading ? 'Verifying…' : 'Verify & Continue'}
            </Button>
          </form>

          <div className="mt-6 text-center space-y-2">
            <button
              data-testid="verify-resend-button"
              onClick={handleResend}
              disabled={resending}
              className="text-[#D4AF37] hover:text-[#F3E5AB] text-sm"
            >
              {resending ? 'Sending…' : "Didn't get the code? Resend"}
            </button>
            <p className="text-[#A3A3A3] text-sm">
              <Link to="/login" className="hover:text-white">← Back to sign in</Link>
            </p>
          </div>
          </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
